import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/layout/DashboardLayout';
import { EmptyState } from '@/components/shared/EmptyState';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import {
  Receipt,
  Plus,
  DollarSign,
  Clock,
  CheckCircle,
  Send,
  AlertCircle,
  Search,
  Eye,
  Check,
  FileText,
  TrendingUp,
  TrendingDown,
  Building2,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-800', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-sky-100 text-sky-800', icon: Check },
  sent: { label: 'Sent', color: 'bg-purple-100 text-purple-800', icon: Send },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800', icon: CheckCircle },
};

export default function FinancePage() {
  const { hasModuleAccess, hasPermission } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [newInvoice, setNewInvoice] = useState({
    customer: '',
    period_start: '',
    period_end: '',
    subtotal: '',
    tax: '',
    line_items: [],
  });
  const [newLineItem, setNewLineItem] = useState({ description: '', amount: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/finance/invoices`),
        axios.get(`${API}/finance/dashboard`),
      ]);
      setInvoices(invoicesRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Failed to fetch finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    try {
      const subtotal = parseFloat(newInvoice.subtotal) || 0;
      const tax = parseFloat(newInvoice.tax) || 0;
      
      await axios.post(`${API}/finance/invoices`, {
        ...newInvoice,
        subtotal,
        tax,
        total: subtotal + tax,
        line_items: newInvoice.line_items,
      });
      setCreateDialogOpen(false);
      setNewInvoice({ customer: '', period_start: '', period_end: '', subtotal: '', tax: '', line_items: [] });
      fetchData();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleApproveInvoice = async () => {
    if (!selectedInvoice) return;
    try {
      await axios.post(`${API}/finance/invoices/${selectedInvoice.id}/approve`);
      setApproveDialogOpen(false);
      setSelectedInvoice(null);
      fetchData();
    } catch (error) {
      console.error('Failed to approve invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve invoice');
    }
  };

  const addLineItem = () => {
    if (newLineItem.description && newLineItem.amount) {
      setNewInvoice({
        ...newInvoice,
        line_items: [...newInvoice.line_items, { ...newLineItem, amount: parseFloat(newLineItem.amount) }],
      });
      setNewLineItem({ description: '', amount: '' });
      
      // Auto-calculate subtotal
      const subtotal = [...newInvoice.line_items, { amount: parseFloat(newLineItem.amount) }]
        .reduce((sum, item) => sum + item.amount, 0);
      setNewInvoice(prev => ({ ...prev, subtotal: subtotal.toString() }));
    }
  };

  const filteredInvoices = invoices.filter((inv) => {
    const matchesSearch = inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.customer?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || inv.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Chart data
  const statusChartData = dashboard ? [
    { name: 'Draft', count: dashboard.invoices_by_status?.draft || 0, color: '#64748b' },
    { name: 'Pending', count: dashboard.invoices_by_status?.pending_approval || 0, color: '#f59e0b' },
    { name: 'Approved', count: dashboard.invoices_by_status?.approved || 0, color: '#0ea5e9' },
    { name: 'Sent', count: dashboard.invoices_by_status?.sent || 0, color: '#8b5cf6' },
    { name: 'Paid', count: dashboard.invoices_by_status?.paid || 0, color: '#10b981' },
  ] : [];

  if (!hasModuleAccess('finance')) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Module Not Available</h2>
            <p className="text-slate-500">The Finance module is not enabled for your organization.</p>
            <p className="text-sm text-slate-400 mt-2">Contact your administrator to enable this feature.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="finance-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Finance & Governance</h1>
            <p className="text-slate-500">Invoice management, approvals, and financial tracking</p>
          </div>
          {hasPermission('invoice.create') && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-invoice-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create New Invoice</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto">
                  <div className="space-y-2">
                    <Label>Customer</Label>
                    <Input
                      placeholder="e.g., State Grid Corporation"
                      value={newInvoice.customer}
                      onChange={(e) => setNewInvoice({ ...newInvoice, customer: e.target.value })}
                      data-testid="invoice-customer-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Period Start</Label>
                      <Input
                        type="date"
                        value={newInvoice.period_start}
                        onChange={(e) => setNewInvoice({ ...newInvoice, period_start: e.target.value })}
                        data-testid="invoice-period-start"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Period End</Label>
                      <Input
                        type="date"
                        value={newInvoice.period_end}
                        onChange={(e) => setNewInvoice({ ...newInvoice, period_end: e.target.value })}
                        data-testid="invoice-period-end"
                      />
                    </div>
                  </div>
                  
                  {/* Line Items */}
                  <div className="space-y-2">
                    <Label>Line Items</Label>
                    <div className="space-y-2">
                      {newInvoice.line_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm">{item.description}</span>
                          <span className="text-sm font-medium">${item.amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Description"
                        value={newLineItem.description}
                        onChange={(e) => setNewLineItem({ ...newLineItem, description: e.target.value })}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={newLineItem.amount}
                        onChange={(e) => setNewLineItem({ ...newLineItem, amount: e.target.value })}
                        className="w-32"
                      />
                      <Button type="button" variant="outline" onClick={addLineItem}>Add</Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Subtotal ($)</Label>
                      <Input
                        type="number"
                        value={newInvoice.subtotal}
                        onChange={(e) => setNewInvoice({ ...newInvoice, subtotal: e.target.value })}
                        data-testid="invoice-subtotal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tax ($)</Label>
                      <Input
                        type="number"
                        value={newInvoice.tax}
                        onChange={(e) => setNewInvoice({ ...newInvoice, tax: e.target.value })}
                        data-testid="invoice-tax"
                      />
                    </div>
                  </div>
                  
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span>${((parseFloat(newInvoice.subtotal) || 0) + (parseFloat(newInvoice.tax) || 0)).toLocaleString()}</span>
                    </div>
                  </div>

                  <Button onClick={handleCreateInvoice} className="w-full" data-testid="create-invoice-submit">
                    Create Invoice
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="stat-total-invoiced">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Invoiced</p>
                  <p className="text-3xl font-bold text-slate-900">
                    ${((dashboard?.total_invoiced || 0) / 1000).toFixed(0)}K
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-total-paid">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Paid</p>
                  <p className="text-3xl font-bold text-emerald-600">
                    ${((dashboard?.total_paid || 0) / 1000).toFixed(0)}K
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-pending-approval">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Approval</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {dashboard?.pending_approval || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-outstanding">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Outstanding</p>
                  <p className="text-3xl font-bold text-red-600">
                    ${((dashboard?.outstanding || 0) / 1000).toFixed(0)}K
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="invoices">
          <TabsList>
            <TabsTrigger value="invoices" data-testid="tab-invoices">
              <Receipt className="w-4 h-4 mr-2" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <TrendingUp className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
          </TabsList>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="mt-6">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search invoices..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        data-testid="invoice-search"
                      />
                    </div>
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[180px]" data-testid="filter-status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Invoices Table */}
            <Card>
              <CardHeader>
                <CardTitle>Invoices</CardTitle>
                <CardDescription>{filteredInvoices.length} invoices found</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Invoice</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Customer</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Period</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Amount</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvoices.map((invoice) => {
                          const StatusIcon = STATUS_CONFIG[invoice.status]?.icon || FileText;
                          return (
                            <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`invoice-${invoice.id}`}>
                              <td className="py-3 px-4">
                                <div>
                                  <p className="font-medium text-slate-900">{invoice.invoice_number}</p>
                                  <p className="text-sm text-slate-500">{new Date(invoice.created_at).toLocaleDateString()}</p>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-slate-400" />
                                  <span className="text-sm text-slate-600">{invoice.customer}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600">
                                {invoice.period_start} - {invoice.period_end}
                              </td>
                              <td className="py-3 px-4">
                                <div>
                                  <p className="font-medium text-slate-900">${invoice.total?.toLocaleString()}</p>
                                  <p className="text-xs text-slate-500">Tax: ${invoice.tax?.toLocaleString()}</p>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={`flex items-center gap-1 w-fit ${STATUS_CONFIG[invoice.status]?.color || 'bg-slate-100'}`}>
                                  <StatusIcon className="w-3 h-3" />
                                  {STATUS_CONFIG[invoice.status]?.label || invoice.status}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="sm">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  {invoice.status === 'pending_approval' && hasPermission('invoice.approve') && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-green-600"
                                      onClick={() => {
                                        setSelectedInvoice(invoice);
                                        setApproveDialogOpen(true);
                                      }}
                                      data-testid={`approve-${invoice.id}`}
                                    >
                                      <Check className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {filteredInvoices.length === 0 && (
                          <tr>
                            <td colSpan={6}>
                              <EmptyState
                                icon={Receipt}
                                title="No invoices found"
                                description="Create your first invoice to start tracking finances"
                              />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Invoice Status Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Invoices by Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={statusChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Bar dataKey="count" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* AR Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Accounts Receivable Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-600">Total Invoiced</span>
                      <span className="font-bold">${(dashboard?.total_invoiced || 0).toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div className="bg-sky-500 h-2 rounded-full" style={{ width: '100%' }} />
                    </div>
                  </div>

                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-emerald-600">Paid</span>
                      <span className="font-bold text-emerald-700">${(dashboard?.total_paid || 0).toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-emerald-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-500 h-2 rounded-full" 
                        style={{ width: `${dashboard?.total_invoiced ? (dashboard.total_paid / dashboard.total_invoiced * 100) : 0}%` }} 
                      />
                    </div>
                  </div>

                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-red-600">Outstanding</span>
                      <span className="font-bold text-red-700">${(dashboard?.outstanding || 0).toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-red-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full" 
                        style={{ width: `${dashboard?.total_invoiced ? (dashboard.outstanding / dashboard.total_invoiced * 100) : 0}%` }} 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{dashboard?.pending_approval || 0}</p>
                      <p className="text-sm text-slate-500">Pending Approval</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">
                        {dashboard?.total_invoiced ? 
                          ((dashboard.total_paid / dashboard.total_invoiced) * 100).toFixed(0) : 0}%
                      </p>
                      <p className="text-sm text-slate-500">Collection Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Approve Dialog */}
        <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve Invoice</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <p className="text-slate-600">
                Are you sure you want to approve invoice <strong>{selectedInvoice?.invoice_number}</strong>?
              </p>
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span>Customer:</span>
                  <span className="font-medium">{selectedInvoice?.customer}</span>
                </div>
                <div className="flex justify-between">
                  <span>Amount:</span>
                  <span className="font-bold text-lg">${selectedInvoice?.total?.toLocaleString()}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setApproveDialogOpen(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleApproveInvoice} data-testid="confirm-approve">
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
